import React from 'react'
import toNumber from 'lodash/toNumber'
import type { Blog, Order, Product, PurchaseOrder } from '@ztechapps/common.contract'
import type { AiChatPageContext } from '@ztechapps/common.contract'
import { getWindow } from '@ztechapps/common.utils'
import type { AiChatProps } from '../ai-chat/AiChat'
import { AiChat } from '../ai-chat/AiChat'
import type { EmptyChatSuggestion } from '../ai-chat/EmptyChat'
import { FloatingChatBubbleProvider, useFloatingChatBubbleContext } from './Context'
import { FloatingChatBubble, AiChatBubblePositionEnum } from './FloatingChatBubble'
import { ChatModal } from './ChatModal'
import { AnchoredChatModal } from './AnchoredChatModal'
import { useChatBubbleOffset } from './useChatBubbleOffset'
import { useResponsiveModalSize } from './useResponsiveModalSize'
import { DEFAULT_ASSISTANT_NAME } from '../constants'

export { AiChatBubblePositionEnum } from './FloatingChatBubble'

export type AiChatBubbleProps = {
  /** Height of the chat modal (overrides responsive default) */
  height?: string
  /** Width of the chat modal (overrides responsive default) */
  width?: string
  /** Whether the modal should be open by default */
  defaultOpen?: boolean
  /** API endpoint for chat - defaults to '/api/ai/chat' */
  api?: string
  /** Storage key for persisting modal state. Defaults to 'ai-chat-bubble-open' */
  storageKey?: string
  /** Optional URL builder for rendering product result cards (per-surface — storefront vs admin) */
  productUrl?: (product: Product) => string
  /** Optional URL builder for rendering blog result list (per-surface — storefront vs admin) */
  blogUrl?: (post: Blog) => string
  /** Optional URL builder for rendering the sales-order "Order Created" message (per-surface — admin only today). */
  orderUrl?: (order: Order) => string
  /** Optional URL builder for rendering the purchase-order "PO Created" message (per-surface — admin only today). */
  purchaseOrderUrl?: (po: PurchaseOrder) => string
  /** When set to a non-empty string, the bubble auto-opens and auto-sends the value as the first user message. */
  autoSendOnOpen?: string
  /** When true, every change to `autoSendOnOpen` creates a fresh session before auto-sending. */
  newSessionOnAutoSend?: boolean
  /** Optional action node rendered inside the error alert and below product results. */
  helpAction?: React.ReactNode
  /** Optional per-surface suggestion cards shown on the empty-chat placeholder. Storefront-style surfaces should pass scope-appropriate cards to avoid advertising admin-only capabilities. */
  emptyChatSuggestions?: EmptyChatSuggestion[]
  /** Page state read at send time and carried on the chat request body so the model sees the open pivot. */
  pageContext?: AiChatPageContext
  /** Corner to anchor the FAB and modal. Defaults to `BottomRight`. */
  position?: AiChatBubblePositionEnum
  /** Admin opt-in: the bubble can be dragged anywhere in the viewport, its spot persists in localStorage, and the chat window opens beside it. */
  isDraggable?: boolean
  /** Name the assistant goes by: window title, message authors, loading indicator and empty chat. Defaults to 'PAPI'. */
  assistantName?: string
  /** Greeting shown under the name in the empty chat. Defaults to the Panel Punches text. */
  greeting?: string
  /** Optional action node rendered beside the credits-exhausted warning (e.g. a link to the AI usage page). */
  creditsAction?: React.ReactNode
  /** Optional node rendered in the chat header under the session dropdown (e.g. the period the assistant reads). Nothing when unset. */
  contextLabel?: React.ReactNode
  /** Whether Ctrl/Cmd+K toggles the chat. Defaults to true; turn off where Ctrl/Cmd+K belongs to the global search. */
  enableKeyboardShortcut?: boolean
  /** Optional children rendered inside the provider tree but outside the chat modal. */
  children?: React.ReactNode
}

export const AiChatBubble = ({
  height,
  width,
  defaultOpen = false,
  api,
  storageKey,
  productUrl,
  blogUrl,
  orderUrl,
  purchaseOrderUrl,
  autoSendOnOpen,
  newSessionOnAutoSend,
  helpAction,
  emptyChatSuggestions,
  pageContext,
  position = AiChatBubblePositionEnum.BottomRight,
  isDraggable = false,
  assistantName,
  greeting,
  creditsAction,
  contextLabel,
  enableKeyboardShortcut,
  children,
}: AiChatBubbleProps) => {
  return (
    <FloatingChatBubbleProvider
      defaultOpen={defaultOpen}
      storageKey={storageKey}
      enableKeyboardShortcut={enableKeyboardShortcut}
    >
      <AiChatBubbleInternal
        height={height}
        width={width}
        api={api}
        productUrl={productUrl}
        blogUrl={blogUrl}
        orderUrl={orderUrl}
        purchaseOrderUrl={purchaseOrderUrl}
        autoSendOnOpen={autoSendOnOpen}
        newSessionOnAutoSend={newSessionOnAutoSend}
        helpAction={helpAction}
        emptyChatSuggestions={emptyChatSuggestions}
        pageContext={pageContext}
        position={position}
        isDraggable={isDraggable}
        assistantName={assistantName}
        greeting={greeting}
        creditsAction={creditsAction}
        contextLabel={contextLabel}
      />
      {children}
    </FloatingChatBubbleProvider>
  )
}

/**
 * Internal component that consumes the FloatingChatBubbleContext.
 * Separated to ensure context is available.
 */
const AiChatBubbleInternal = ({
  height,
  width,
  api,
  productUrl,
  blogUrl,
  orderUrl,
  purchaseOrderUrl,
  autoSendOnOpen,
  newSessionOnAutoSend,
  helpAction,
  emptyChatSuggestions,
  pageContext,
  position = AiChatBubblePositionEnum.BottomRight,
  isDraggable = false,
  assistantName = DEFAULT_ASSISTANT_NAME,
  greeting,
  creditsAction,
  contextLabel,
}: Omit<AiChatBubbleProps, 'defaultOpen' | 'storageKey'>) => {
  const { isOpen, close, isMaximized, toggleMaximized } = useFloatingChatBubbleContext()
  const responsiveSize = useResponsiveModalSize(isMaximized)
  const bubbleRef = React.useRef<HTMLButtonElement>(null)
  const { offset, bounds, saveOffset, resetOffset } = useChatBubbleOffset({ bubbleRef })

  // Caller-provided dimensions are honored only when NOT maximized — when the
  // user explicitly maximizes, the responsive 95vw / 92vh wins so the modal
  // visibly grows regardless of any default the call site passes in.
  const modalWidth = !isMaximized && width ? width : responsiveSize.width
  const modalHeight = !isMaximized && height ? height : responsiveSize.height

  // Extract numeric height for AiChat component (remove 'px', 'vh', etc.)
  const numericHeight = React.useMemo(() => {
    if (!modalHeight) return 600

    // If it's a pixel value, extract the number (strip 'px' suffix - toNumber rejects units)
    if (modalHeight.endsWith('px')) {
      const pixels = toNumber(modalHeight.slice(0, -2))
      return isNaN(pixels) ? 600 : pixels
    }

    // If it's a vh value, calculate approximate pixels (viewport height * percentage)
    if (modalHeight.endsWith('vh')) {
      const percentage = toNumber(modalHeight.slice(0, -2))
      if (isNaN(percentage)) return 600
      const win = getWindow()
      if (!win) return 600
      // Approximate viewport height - will be overridden by actual container height
      return Math.floor((win.innerHeight * percentage) / 100)
    }

    // Default fallback
    return 600
  }, [modalHeight])

  // AiChat props (omit 'children' from AiChatProps)
  const aiChatProps: AiChatProps = {
    height: numericHeight,
    api,
    productUrl,
    blogUrl,
    orderUrl,
    purchaseOrderUrl,
    autoSendOnOpen,
    newSessionOnAutoSend,
    helpAction,
    emptyChatSuggestions,
    pageContext,
    assistantName,
    greeting,
    creditsAction,
    contextLabel,
  }

  // Chat content is built once — the window keeps it mounted across close
  // and reopen, so the conversation survives whichever modal hosts it.
  const chat = <AiChat {...aiChatProps} />

  return (
    <>
      {/* Floating Action Button - Z-index 1050 */}
      <FloatingChatBubble
        position={position}
        bubbleRef={bubbleRef}
        isDraggable={isDraggable}
        offset={offset}
        bounds={bounds}
        onOffsetSave={saveOffset}
      />

      {/* Chat Modal - Z-index 1060 */}
      {isDraggable ? (
        <AnchoredChatModal
          isOpen={isOpen}
          onClose={close}
          width={modalWidth}
          height={modalHeight}
          position={position}
          isMaximized={isMaximized}
          onToggleMaximized={toggleMaximized}
          bubbleRef={bubbleRef}
          bubbleOffset={offset}
          onResetPosition={resetOffset}
          title={assistantName}
        >
          {chat}
        </AnchoredChatModal>
      ) : (
        <ChatModal
          isOpen={isOpen}
          onClose={close}
          width={modalWidth}
          height={modalHeight}
          position={position}
          isMaximized={isMaximized}
          onToggleMaximized={toggleMaximized}
          title={assistantName}
        >
          {chat}
        </ChatModal>
      )}
    </>
  )
}
